/**
 * DeploySuccess — success screen shown after token deployment
 *
 * Shows token info, explorer links, and navigation actions.
 * Clears sessionStorage draft on mount.
 *
 * Story 2.5 Task 6
 */

import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useWizardDraft } from "../../hooks/use-wizard-draft";
import { formatAddress } from "../../lib/format";
import { Button } from "../ui/Button";

type DeploySuccessProps = {
	tokenName: string;
	tokenSymbol: string;
	contractAddress: `0x${string}`;
	poolAddress?: `0x${string}` | undefined;
	tokenId: string;
	blockExplorerUrl: string;
	airdropRecipientCount?: number | undefined;
	airdropRegistered?: boolean | undefined;
	presaleEnabled?: boolean | undefined;
	presaleStarted?: boolean | undefined;
};

export function DeploySuccess({
	tokenName,
	tokenSymbol,
	contractAddress,
	poolAddress,
	tokenId,
	blockExplorerUrl,
	airdropRecipientCount,
	airdropRegistered,
	presaleEnabled,
	presaleStarted,
}: DeploySuccessProps) {
	const { clearDraft } = useWizardDraft();
	const draftCleared = useRef(false);
	const [copied, setCopied] = useState(false);

	// Clear draft once on mount
	useEffect(() => {
		if (!draftCleared.current) {
			draftCleared.current = true;
			clearDraft();
		}
	}, [clearDraft]);

	const tokenUrl = `${globalThis.location.origin}/token/${tokenId}`;

	const handleShare = () => {
		navigator.clipboard.writeText(tokenUrl).then(
			() => {
				setCopied(true);
				setTimeout(() => {
					setCopied(false);
				}, 2000);
			},
			() => {
				// Clipboard API may fail in insecure contexts — silently ignore
			}
		);
	};

	return (
		<div className="relative overflow-hidden rounded-2xl bg-white p-8 text-center shadow-lg">
			{/* CSS Confetti */}
			<div className="confetti-container" aria-hidden="true">
				{Array.from({ length: 20 }, (_, i) => (
					<div
						key={`confetti-${String(i)}`}
						className="confetti-piece"
						style={{
							left: `${String(Math.random() * 100)}%`,
							animationDelay: `${String(Math.random() * 2)}s`,
							backgroundColor: ["#f59e0b", "#3b82f6", "#10b981", "#ef4444", "#8b5cf6"][i % 5],
						}}
					/>
				))}
			</div>

			{/* Success Icon */}
			<div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
				<svg
					className="h-8 w-8 text-green-500"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
					role="img"
					aria-label="Success checkmark"
				>
					<title>Success</title>
					<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
				</svg>
			</div>

			<h2 className="mb-2 text-2xl font-bold text-pastel-black">Token Deployed!</h2>
			<p className="mb-6 text-pastel-gray">
				{tokenName} ({tokenSymbol}) is now live on-chain
			</p>

			{/* Token Details */}
			<div className="mb-8 space-y-3 rounded-xl bg-pastel-light-gray/50 p-4 text-left">
				<div className="flex items-center justify-between">
					<span className="text-sm text-pastel-gray">Contract</span>
					<a
						href={`${blockExplorerUrl}/address/${contractAddress}`}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm font-mono text-pastel-vibrant-blue hover:underline"
					>
						{formatAddress(contractAddress)} ↗
					</a>
				</div>
				{poolAddress ? (
					<div className="flex items-center justify-between">
						<span className="text-sm text-pastel-gray">Pool</span>
						<a
							href={`${blockExplorerUrl}/address/${poolAddress}`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm font-mono text-pastel-vibrant-blue hover:underline"
						>
							{formatAddress(poolAddress)} ↗
						</a>
					</div>
				) : null}
			</div>

			{/* Airdrop Banner */}
			{airdropRecipientCount != null &&
				airdropRecipientCount > 0 &&
				(airdropRegistered !== false ? (
					<div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4 text-left">
						<p className="text-sm font-medium text-green-800">
							Airdrop configured for {airdropRecipientCount} recipient
							{airdropRecipientCount === 1 ? "" : "s"}
						</p>
						<p className="text-xs text-green-600 mt-1">
							Recipients can claim their tokens once the airdrop is live.
						</p>
					</div>
				) : (
					<div className="mb-6 rounded-lg bg-amber-50 border border-amber-200 p-4 text-left">
						<p className="text-sm font-medium text-amber-800">Airdrop registration failed</p>
						<p className="text-xs text-amber-600 mt-1">
							Your token was deployed successfully, but the airdrop for {airdropRecipientCount}{" "}
							recipient
							{airdropRecipientCount === 1 ? "" : "s"} could not be registered on-chain. You can
							register the airdrop manually later.
						</p>
					</div>
				))}

			{/* Presale Banner */}
			{presaleEnabled &&
				(presaleStarted ? (
					<div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4 text-left">
						<p className="text-sm font-medium text-green-800">Presale started</p>
						<p className="text-xs text-green-600 mt-1">
							Your token presale is now live. Buyers can participate until the goal is reached or
							time expires.
						</p>
					</div>
				) : (
					<div className="mb-6 rounded-lg bg-amber-50 border border-amber-200 p-4 text-left">
						<p className="text-sm font-medium text-amber-800">Presale not started</p>
						<p className="text-xs text-amber-600 mt-1">
							Your token was deployed with presale supply reserved, but the presale could not be
							started automatically. You can start the presale manually.
						</p>
					</div>
				))}

			{/* Actions */}
			<div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
				<Link to="/token/$tokenId" params={{ tokenId }}>
					<Button>View Token</Button>
				</Link>
				<Button variant="outline" onClick={handleShare}>
					{copied ? "Link Copied!" : "Share"}
				</Button>
				<Link to="/launch">
					<Button variant="outline">Create Another</Button>
				</Link>
			</div>

			{/* Confetti animation styles */}
			<style>{`
				.confetti-container {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					pointer-events: none;
					overflow: hidden;
				}
				.confetti-piece {
					position: absolute;
					top: -10px;
					width: 8px;
					height: 8px;
					border-radius: 2px;
					opacity: 0;
					animation: confetti-fall 3s ease-in-out forwards;
				}
				@keyframes confetti-fall {
					0% {
						opacity: 1;
						transform: translateY(0) rotate(0deg);
					}
					100% {
						opacity: 0;
						transform: translateY(400px) rotate(720deg);
					}
				}
			`}</style>
		</div>
	);
}
